import { useState } from 'react';
import { api, errorText, get, newKey, post } from './api.js';
import { t } from './i18n.js';
import { Link } from './router.jsx';
import { useAuth } from './session.jsx';
import { Check, Field, L, Loadable, Pager, Seg, dateOnly, dateText, money, useLoad, useToast } from './ui.jsx';

const Back = ({ to = '/me', children }) => <Link to={to} className="small">{children || t('กลับไปบัญชีของฉัน', 'Back to my account')}</Link>;
// The sandbox provider takes a fixed test token. A real provider replaces this with the token from its card form.
const TEST_TOKEN = 'tok_test';

function PaymentNote({ config }) {
  if (!config) return null;
  if (config.test_mode) return <div className="note">{t('โหมดทดสอบ: ยังไม่มีการตัดเงินจริง ทุกรายการสำเร็จทันทีเพื่อให้ลองใช้ได้ครบทุกขั้นตอน', 'Test mode: no money is taken. Every payment succeeds at once so the whole flow can be tried.')}</div>;
  if (!config.payments_enabled) return <div className="note">{t('ระบบชำระเงินยังไม่เปิดให้บริการ ตอนนี้ทุกบัญชีใช้แพ็กเกจฟรี', 'Payments are not open yet. Every account is on the free plan for now.')}</div>;
  return null;
}

/* ---------- plans ---------- */
export function Plans() {
  const toast = useToast();
  const { reloadUser } = useAuth();
  const [interval, setInterval_] = useState('month');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const plans = useLoad(() => get('/v1/subscriptions/plans'), []);
  const current = useLoad(() => get('/v1/subscriptions/current'), []);
  const config = useLoad(() => get('/v1/billing/config'), []);
  const cfg = config.data?.data;
  const sub = current.data?.data;
  const live = sub && ['trialing', 'active', 'past_due'].includes(sub.status);
  const after = async (message) => { await reloadUser(); current.reload(); setConfirm(null); toast(message); };
  const choose = async (p) => {
    setBusy(true);
    try { await post('/v1/subscriptions', { plan: p.plan, billing_interval: interval, payment_token: TEST_TOKEN }, { idem: newKey() }); await after(t(`เปลี่ยนเป็นแพ็กเกจ ${L.plan[p.plan]} แล้ว`, `You are now on ${L.plan[p.plan]}`)); }
    catch (e) { toast(e.code === 'payment_failed' && cfg?.payments_enabled ? t('ชำระเงินไม่สำเร็จ กรุณาลองอีกครั้ง', 'The payment did not go through. Please try again.') : errorText(e)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await post('/v1/subscriptions/current/cancel'); await after(t('ยกเลิกแล้ว ใช้แพ็กเกจเดิมได้จนสิ้นรอบ', 'Cancelled. Your plan stays until the period ends.')); }
    catch (e) { toast(errorText(e)); } finally { setBusy(false); }
  };
  const contact = { chat_only: t('แชตเท่านั้น', 'Chat only'), phone: t('แชตและโทรศัพท์', 'Chat and phone'), phone_and_email: t('แชต โทรศัพท์ และอีเมล', 'Chat, phone and email') };
  const rank = [t('ปกติ', 'Standard'), t('เหนือฟรี', 'Above Free'), t('เหนือ Verified', 'Above Verified'), t('บนสุด', 'Top')];
  const statusLine = sub && {
    trialing: t(`ทดลองใช้ฟรีถึง ${dateOnly(sub.current_period_end)} จากนั้นต่ออายุอัตโนมัติ`, `Free trial until ${dateOnly(sub.current_period_end)}, then it renews automatically`),
    active: t(`ต่ออายุอัตโนมัติวันที่ ${dateOnly(sub.current_period_end)}`, `Renews on ${dateOnly(sub.current_period_end)}`),
    past_due: t('ชำระเงินรอบล่าสุดไม่สำเร็จ แพ็กเกจจะสิ้นสุดใน 7 วันหากยังไม่ได้ชำระ', 'The last payment failed. The plan ends in 7 days unless it is paid.'),
    cancelled: t(`ยกเลิกแล้ว ใช้ได้ถึง ${dateOnly(sub.current_period_end)} จากนั้นกลับเป็นแพ็กเกจฟรี`, `Cancelled. Active until ${dateOnly(sub.current_period_end)}, then back to Free`),
  }[sub.status];
  return (
    <div className="page">
      <Back />
      <h1>{t('แพ็กเกจสมาชิก', 'Membership plans')}</h1>
      <PaymentNote config={cfg} />
      {sub && (
        <div className="card row between">
          <div><span className="muted small">{t('แพ็กเกจปัจจุบัน', 'Current plan')}</span><h2>{L.plan[sub.effective_plan]}{sub.billing_interval && sub.effective_plan !== 'free' && <span className="muted small"> {L.interval[sub.billing_interval]}</span>}</h2>
            {statusLine && <p className="muted small num">{statusLine}</p>}</div>
          {live && <button className="btn danger s" disabled={busy} onClick={cancel}>{t('ยกเลิกแพ็กเกจ', 'Cancel plan')}</button>}
        </div>
      )}
      <Seg label={t('รอบการชำระเงิน', 'Billing period')} value={interval} onChange={(v) => { setInterval_(v); setConfirm(null); }} options={[['week', t('รายสัปดาห์', 'Weekly')], ['month', t('รายเดือน', 'Monthly')], ['year', t('รายปี', 'Yearly')]]} />
      <Loadable state={plans}>{({ data }) => {
        const cols = data.filter((p) => p.billing_interval === interval);
        const isCurrent = (p) => sub?.effective_plan === p.plan && (p.plan === 'free' || (sub.billing_interval === interval && sub.status !== 'cancelled'));
        return (
          <>
            <div className="plans">
              {cols.map((p) => {
                const rec = p.plan === 'pro';
                const lines = [
                  t(`ออนไลน์พร้อมกัน ${p.limits.active_listings.toLocaleString('en-US')} ประกาศ`, `${p.limits.active_listings.toLocaleString('en-US')} listing${p.limits.active_listings === 1 ? '' : 's'} online at once`),
                  t(`ลำดับในผลค้นหา: ${rank[p.limits.listing_priority]}`, `Search ranking: ${rank[p.limits.listing_priority]}`),
                  p.limits.listing_priority >= 2 && t('ป้าย “แนะนำ” สีทองบนประกาศ', 'Gold “Featured” badge on listings'),
                  t(`ช่องทางติดต่อบนประกาศ: ${contact[p.limits.contact_visibility]}`, `Contact channels on listings: ${contact[p.limits.contact_visibility]}`),
                  (p.limits.lead_access === 'chat_only' ? t('ข้อมูลผู้สนใจ: แชตเท่านั้น', 'Enquirer details: chat only') : t('ข้อมูลผู้สนใจ: โทรศัพท์และอีเมล', 'Enquirer details: phone and email')),
                  p.limits.ownership_verifications_per_month ? t(`ตรวจเอกสารสิทธิ์ฟรี ${p.limits.ownership_verifications_per_month} ประกาศต่อเดือน`, `${p.limits.ownership_verifications_per_month} free ownership checks per month`) : t(`ตรวจเอกสารสิทธิ์ ${cfg?.ownership_verification_cost_credits ?? 5} เครดิตต่อประกาศ`, `Ownership check: ${cfg?.ownership_verification_cost_credits ?? 5} credits per listing`),
                ].filter(Boolean);
                return (
                  <article className={`plan${rec ? ' rec' : ''}`} key={p.plan} aria-label={t(`แพ็กเกจ ${L.plan[p.plan]}`, `${L.plan[p.plan]} plan`)}>
                    {rec && <span className="tag">{t('แนะนำ', 'Recommended')}</span>}
                    <h3>{L.plan[p.plan]}</h3>
                    <div className="amt num"><b>{money(p.price)}</b>{p.price > 0 && <span className="muted small">{L.interval[interval]}</span>}</div>
                    <ul>{lines.map((x) => <li key={x}><Check />{x}</li>)}</ul>
                    {isCurrent(p) ? <span className="pill ok" style={{ alignSelf: 'flex-start' }}>{t('แพ็กเกจปัจจุบัน', 'Current plan')}</span>
                      : p.plan !== 'free' && <button className={`btn ${rec ? 'gold' : 'ghost'}`} disabled={busy || (cfg && !cfg.payments_enabled)} onClick={() => setConfirm(p)}>{t(`เลือก ${L.plan[p.plan]}`, `Choose ${L.plan[p.plan]}`)}</button>}
                  </article>
                );
              })}
            </div>
            {confirm && (
              <div className="card stack" role="dialog" aria-label={t('ยืนยันการเลือกแพ็กเกจ', 'Confirm plan')}>
                <h2>{t(`ยืนยันแพ็กเกจ ${L.plan[confirm.plan]}`, `Confirm ${L.plan[confirm.plan]}`)}</h2>
                <p className="num">{sub && !sub.trial_used
                  ? t(`ทดลองใช้ฟรี ${cfg?.trial_days ?? 14} วัน จากนั้น ${money(confirm.price)} ${L.interval[interval]} ยกเลิกได้ทุกเมื่อ`, `Free for ${cfg?.trial_days ?? 14} days, then ${money(confirm.price)} ${L.interval[interval]}. Cancel any time.`)
                  : t(`ชำระ ${money(confirm.price)} ${L.interval[interval]} เริ่มรอบใหม่วันนี้ รอบเดิมที่เหลือไม่คืนเงิน`, `${money(confirm.price)} ${L.interval[interval]}. A new period starts today; the rest of the current period is not refunded.`)}</p>
                {sub && confirm.limits.active_listings < sub.limits.active_listings && <div className="note">{t(`แพ็กเกจนี้ออนไลน์ได้ ${confirm.limits.active_listings} ประกาศ ประกาศที่เกินจะถูกหยุดชั่วคราว เริ่มจากประกาศล่าสุด`, `This plan allows ${confirm.limits.active_listings} listings online. Any above that are paused, newest first.`)}</div>}
                <div className="row"><button className="btn primary" disabled={busy} onClick={() => choose(confirm)}>{t('ยืนยัน', 'Confirm')}</button><button className="btn ghost" disabled={busy} onClick={() => setConfirm(null)}>{t('ยกเลิก', 'Back')}</button></div>
              </div>
            )}
          </>
        );
      }}</Loadable>
      <p className="muted small">{t('ต้องการดันประกาศหรือตรวจเอกสารสิทธิ์เพิ่ม ', 'Need to boost a listing or check more title documents? ')}<Link to="/me/credits">{t('ดูเครดิต', 'See credits')}</Link></p>
    </div>
  );
}

/* ---------- credits ---------- */
export function Credits() {
  const toast = useToast();
  const { reloadUser } = useAuth();
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState('');
  const config = useLoad(() => get('/v1/billing/config'), []);
  const ledger = useLoad(() => get('/v1/credits', { page }), [page]);
  const cfg = config.data?.data;
  const buy = async (pack) => {
    setBusy(pack.package);
    try { await post('/v1/credits/purchase', { package: pack.package, payment_token: TEST_TOKEN }, { idem: newKey() }); await reloadUser(); ledger.reload(); toast(t(`เติม ${pack.credits} เครดิตแล้ว`, `${pack.credits} credits added`)); }
    catch (e) { toast(e.code === 'payment_failed' && cfg?.payments_enabled ? t('ชำระเงินไม่สำเร็จ กรุณาลองอีกครั้ง', 'The payment did not go through. Please try again.') : errorText(e)); }
    finally { setBusy(''); }
  };
  const kind = { purchase: t('เติมเครดิต', 'Credits bought'), boost: t('ดันประกาศ', 'Listing boost'), ownership_verification: t('ตรวจเอกสารสิทธิ์', 'Ownership check') };
  return (
    <div className="page" style={{ maxWidth: 720, marginInline: 'auto', width: '100%' }}>
      <Back />
      <h1>{t('เครดิต', 'Credits')}</h1>
      <PaymentNote config={cfg} />
      <div className="card stack">
        <div className="balance num"><b>{ledger.data ? ledger.data.meta.credit_balance.toLocaleString('en-US') : ''}</b><span className="muted">{t('เครดิตคงเหลือ', 'credits left')}</span></div>
        {cfg && <p className="muted small">{t(`ดันประกาศขึ้นบนสุดของกลุ่มเดียวกัน ${cfg.boost_cost_credits} เครดิตต่อครั้ง ตรวจเอกสารสิทธิ์นอกโควตาแพ็กเกจ ${cfg.ownership_verification_cost_credits} เครดิตต่อประกาศ เครดิตไม่มีวันหมดอายุและไม่คืนเงิน`, `A boost moves a listing to the top of its tier for ${cfg.boost_cost_credits} credit. An ownership check outside your plan quota costs ${cfg.ownership_verification_cost_credits} credits per listing. Credits do not expire and are not refundable.`)}</p>}
      </div>
      <section className="stack">
        <h2>{t('เติมเครดิต', 'Buy credits')}</h2>
        <Loadable state={config}>{({ data }) => (
          <div className="packs">
            {data.credit_packages.map((p) => (
              <div className="pack" key={p.package}>
                <b className="num">{p.credits} {t('เครดิต', 'credits')}</b>
                <span className="muted small num">{money(p.price)} ({money(p.price / p.credits)} {t('ต่อเครดิต', 'each')})</span>
                <button className="btn primary s" disabled={!!busy || !data.payments_enabled} onClick={() => buy(p)}>{busy === p.package ? t('กำลังทำรายการ', 'Working') : t('ซื้อ', 'Buy')}</button>
              </div>
            ))}
          </div>
        )}</Loadable>
      </section>
      <section className="stack">
        <h2>{t('ประวัติเครดิต', 'Credit history')}</h2>
        <Loadable state={ledger}>{({ data, meta }) => data.length === 0 ? <div className="empty">{t('ยังไม่มีรายการ', 'No transactions yet')}</div> : (
          <div className="card stack">
            <div className="ledger num">
              {data.map((x) => (
                <div key={x.id}><span>{kind[x.type] || x.type}<br /><span className="muted small">{dateText(x.created_at)}</span></span>
                  <span style={{ textAlign: 'right' }}><span className={x.amount > 0 ? 'plus' : 'minus'}>{x.amount > 0 ? '+' : ''}{x.amount}</span><br /><span className="muted small">{t('คงเหลือ', 'Balance')} {x.balance_after}</span></span></div>
              ))}
            </div>
            <Pager meta={meta} onPage={setPage} />
          </div>
        )}</Loadable>
      </section>
    </div>
  );
}

/* ---------- ownership verification for one listing ---------- */
export function Ownership({ params }) {
  const { user, reloadUser } = useAuth();
  const toast = useToast();
  const docType = user.role === 'owner' ? 'title_deed' : 'sale_authorization';
  const listing = useLoad(() => get(`/v1/properties/${params.id}`), [params.id]);
  const quote = useLoad(() => get(`/v1/properties/${params.id}/verifications/quote`), [params.id]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError(t('เลือกไฟล์เอกสาร', 'Choose the document file'));
    if (file.size > 10 * 1024 * 1024) return setError(t('ไฟล์ใหญ่เกิน 10 MB', 'The file is larger than 10 MB'));
    setBusy(true); setError('');
    const form = new FormData();
    form.append('document_type', docType); form.append('file', file, file.name);
    try { await api('POST', `/v1/properties/${params.id}/verifications`, { form }); await reloadUser(); quote.reload(); toast(t('ส่งเอกสารแล้ว ทีมงานจะตรวจสอบและแจ้งผล', 'Document sent. We will review it and let you know.')); }
    catch (er) { setError(er.code === 'unsupported_media_type' ? t('รองรับเฉพาะไฟล์ JPEG, PNG และ PDF', 'Only JPEG, PNG and PDF files are accepted') : er.code === 'insufficient_credits' ? t('เครดิตไม่พอ กรุณาเติมเครดิตก่อน', 'Not enough credits. Please buy credits first.') : errorText(er)); }
    finally { setBusy(false); }
  };
  return (
    <div className="page" style={{ maxWidth: 620, marginInline: 'auto', width: '100%' }}>
      <Back to="/me/listings">{t('กลับไปประกาศของฉัน', 'Back to my listings')}</Back>
      <h1>{t('ตรวจเอกสารสิทธิ์', 'Ownership check')}</h1>
      <p className="muted">{t('ประกาศที่ผ่านการตรวจเอกสารสิทธิ์จะได้ป้าย “ตรวจเอกสารสิทธิ์แล้ว” และแสดงเมื่อผู้ซื้อกรองเฉพาะประกาศที่ตรวจแล้ว เอกสารเก็บเป็นความลับ มีเพียงทีมตรวจสอบที่เปิดดูได้', 'A listing that passes gets the “Ownership checked” badge and appears when buyers filter for checked listings. The document stays private; only the review team can open it.')}</p>
      <Loadable state={listing}>{({ data }) => <div className="card"><strong>{data.title || t('ฉบับร่างยังไม่มีหัวข้อ', 'Untitled draft')}</strong><br /><span className="muted small">{[data.address_line_1, data.city, data.state].filter(Boolean).join(' ')}</span></div>}</Loadable>
      <Loadable state={quote}>{({ data }) => {
        const st = data.ownership_verification_status;
        if (st === 'approved') return <div className="note ok">{t('ประกาศนี้ผ่านการตรวจเอกสารสิทธิ์แล้ว', 'This listing has passed the ownership check')}</div>;
        if (st === 'pending') return <div className="note">{t('ส่งเอกสารแล้ว กำลังรอทีมงานตรวจสอบ เราจะแจ้งเตือนเมื่อทราบผล', 'Document sent and waiting for review. We will notify you with the result.')}</div>;
        const short = data.credits_required > data.credit_balance;
        return (
          <form className="card stack" onSubmit={submit} noValidate>
            {st === 'rejected' && <div className="note bad">{t('เอกสารครั้งก่อนไม่ผ่าน ส่งใหม่ได้โดยไม่มีค่าใช้จ่าย', 'The last document was not accepted. You can send a new one at no cost.')}</div>}
            <div className="note num">{data.resubmission ? t('ส่งใหม่ไม่มีค่าใช้จ่าย', 'Resubmitting is free')
              : data.uses_plan_quota ? t(`รวมในแพ็กเกจ ใช้ไปแล้ว ${data.quota_used} จาก ${data.quota_per_month} ประกาศในเดือนนี้`, `Included in your plan: ${data.quota_used} of ${data.quota_per_month} used this month`)
                : t(`ค่าบริการ ${data.credits_required} เครดิต คุณมี ${data.credit_balance} เครดิต หักครั้งเดียวต่อประกาศ ไม่คืนเมื่อเอกสารไม่ผ่าน`, `Costs ${data.credits_required} credits; you have ${data.credit_balance}. Charged once per listing and not refunded if the document is rejected.`)}
              {short && <> <Link to="/me/credits">{t('เติมเครดิต', 'Buy credits')}</Link></>}</div>
            <Field label={t(`${L.document[docType]} (JPEG, PNG หรือ PDF ไม่เกิน 10 MB)`, `${L.document[docType]} (JPEG, PNG or PDF, up to 10 MB)`)} id="o-file" error={error}><input id="o-file" type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} /></Field>
            <button className="btn primary" type="submit" disabled={busy || short}>{t('ส่งเอกสาร', 'Send document')}</button>
          </form>
        );
      }}</Loadable>
    </div>
  );
}
